import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { PermissionsTokenService } from './permissions-token.service';
import { SECTION_PERMISSION_MAP as SECTION_PERMISSION_MAP_IMPORT } from './permissions-map';

export interface PermissionInfo {
  code: string;
  description: string;
  subject: string;
  action: 'create' | 'read' | 'update' | 'delete';
}

export interface SectionPermissions {
  section: string;
  permissions: PermissionInfo[];
  hasAnyPermission: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private permissionsTokenService = inject(PermissionsTokenService);
  private readonly TOKEN_KEY = 'auth_token';

  private userPermissionsSubject = new BehaviorSubject<string[]>([]);
  public userPermissions$ = this.userPermissionsSubject.asObservable();

  constructor() {
    this.loadPermissions();
  }

  /**
   * Obtiene el token directamente de sessionStorage para evitar dependencia circular
   */
  private getToken(): string | null {
    return sessionStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Carga los permisos del usuario desde el token
   */
  loadPermissions(): void {
    const token = this.getToken();
    const permissions = this.permissionsTokenService.decodePermissions(token);
    this.userPermissionsSubject.next(permissions || []);
  }

  /**
   * Obtiene los códigos de permisos del usuario actual
   */
  getUserPermissions(): string[] {
    return this.userPermissionsSubject.value;
  }

  /**
   * Verifica si el usuario tiene un permiso específico
   * @param code Código del permiso (ej: '0101')
   */
  hasPermission(code: string): boolean {
    const permissions = this.getUserPermissions();
    return permissions.includes(code);
  }

  /**
   * Verifica si el usuario tiene alguno de los permisos especificados
   * @param codes Array de códigos de permisos
   */
  hasAnyPermission(codes: string[]): boolean {
    const userPermissions = this.getUserPermissions();
    return codes.some((code) => userPermissions.includes(code));
  }

  /**
   * Verifica si el usuario tiene todos los permisos especificados
   * @param codes Array de códigos de permisos
   */
  hasAllPermissions(codes: string[]): boolean {
    const userPermissions = this.getUserPermissions();
    return codes.every((code) => userPermissions.includes(code));
  }

  /**
   * Verifica si el usuario tiene algún permiso de una sección específica
   * @param sectionName Nombre de la sección (ej: 'Usuarios', 'Clientes')
   */
  hasSectionPermission(sectionName: string): boolean {
    const codes = this.SECTION_PERMISSION_MAP[sectionName];
    if (!codes) return false;

    const userPermissions = this.getUserPermissions();
    return !!(
      (codes.create && userPermissions.includes(codes.create)) ||
      (codes.read && userPermissions.includes(codes.read)) ||
      (codes.update && userPermissions.includes(codes.update)) ||
      (codes.delete && userPermissions.includes(codes.delete))
    );
  }

  /**
   * Obtiene los códigos de permisos de una sección específica
   * @param sectionName Nombre de la sección
   */
  getSectionPermissionCodes(sectionName: string): string[] {
    const codes = this.SECTION_PERMISSION_MAP[sectionName];
    if (!codes) return [];

    const permissionCodes: string[] = [];
    if (codes.create) permissionCodes.push(codes.create);
    if (codes.read) permissionCodes.push(codes.read);
    if (codes.update) permissionCodes.push(codes.update);
    if (codes.delete) permissionCodes.push(codes.delete);

    return permissionCodes;
  }

  /**
   * Verifica si el usuario tiene un permiso específico de una sección y acción
   * @param sectionName Nombre de la sección
   * @param action Acción (create, read, update, delete)
   */
  hasSectionAction(
    sectionName: string,
    action: 'create' | 'read' | 'update' | 'delete'
  ): boolean {
    const codes = this.SECTION_PERMISSION_MAP[sectionName];
    if (!codes) return false;

    const permissionCode = codes[action];
    if (!permissionCode) return false;

    return this.hasPermission(permissionCode);
  }

  /**
   * Obtiene el código de permiso para una sección y acción específica
   * @param sectionName Nombre de la sección
   * @param action Acción (create, read, update, delete)
   */
  getPermissionCode(
    sectionName: string,
    action: 'create' | 'read' | 'update' | 'delete'
  ): string | null {
    const codes = this.SECTION_PERMISSION_MAP[sectionName];
    if (!codes) return null;

    return codes[action] || null;
  }

  /**
   * Mapea rutas a secciones para verificación de permisos
   */
  getRouteSection(route: string): string | null {
    const routeSectionMap: { [key: string]: string } = {
      '/home/users': 'Usuarios',
      '/home/clients': 'Clientes',
      '/home/vehicle': 'Vehiculos',
      '/home/claims': 'Reclamos',
      '/home/diagnosis': 'Diagnosticos',
      '/home/quotes': 'Cotizacion',
      '/home/appointments': 'Citas',
      '/home/receptions': 'Recepciones',
      '/home/workorders': 'Ordenes de Trabajos',
      '/home/items': 'Maestro de Articulos',
      '/home/purchaseorders': 'Orden de Compra',
      '/home/deliverynotes': 'Conduce',
      '/home/parts': 'Piezas',
      '/home/purchase-inventory': 'Materiales',
      '/home/returns': 'Devoluciones',
      '/home/reports': 'Reportes',
      '/home/dashboard': 'Reportes',
      '/home/insurance': 'Aseguradoras',
      '/home/suppliers': 'Proveedores',
      '/home/company': 'Empresa',
      '/home/servicescases': 'Casos',
      '/home/roles': 'Roles',
    };

    // Buscar coincidencia exacta primero
    if (routeSectionMap[route]) {
      return routeSectionMap[route];
    }

    // Buscar coincidencia parcial
    for (const [key, value] of Object.entries(routeSectionMap)) {
      if (route.startsWith(key)) {
        return value;
      }
    }

    return null;
  }

  /**
   * Verifica si el usuario puede acceder a una ruta
   * @param route Ruta a verificar
   */
  canAccessRoute(route: string): boolean {
    // Rutas públicas que no requieren permisos específicos
    const publicRoutes = ['/home', '/home/settings'];
    if (publicRoutes.includes(route)) {
      return true;
    }

    const section = this.getRouteSection(route);
    if (!section) {
      // Si no hay mapeo, permitir acceso (por seguridad, podría ser false)
      return true;
    }

    // Para acceder a una ruta, se necesita al menos permiso de lectura
    return this.hasSectionAction(section, 'read');
  }

  /**
   * Mapeo de secciones a códigos de permisos (importado desde archivo centralizado)
   */
  private readonly SECTION_PERMISSION_MAP = SECTION_PERMISSION_MAP_IMPORT;

  /**
   * Verifica si el usuario puede crear en una sección
   * @param section Nombre de la sección (ej: 'Usuarios', 'Empresa')
   */
  canCreate(section: string): boolean {
    const codes = this.SECTION_PERMISSION_MAP[section];
    if (!codes || !codes.create) return true; // Si no hay mapeo o no tiene create, permitir por defecto
    return this.hasPermission(codes.create);
  }

  /**
   * Verifica si el usuario puede editar en una sección
   * @param section Nombre de la sección (ej: 'Usuarios', 'Empresa')
   */
  canUpdate(section: string): boolean {
    const codes = this.SECTION_PERMISSION_MAP[section];
    if (!codes || !codes.update) return true; // Si no hay mapeo o no tiene update, permitir por defecto
    return this.hasPermission(codes.update);
  }

  /**
   * Verifica si el usuario puede eliminar en una sección
   * @param section Nombre de la sección (ej: 'Usuarios', 'Empresa')
   */
  canDelete(section: string): boolean {
    const codes = this.SECTION_PERMISSION_MAP[section];
    if (!codes || !codes.delete) return true; // Si no hay mapeo o no tiene delete, permitir por defecto
    return this.hasPermission(codes.delete);
  }

  /**
   * Filtra acciones de editar y eliminar según permisos
   * @param actions Array de acciones
   * @param section Nombre de la sección
   */
  filterActionsByPermissions(actions: any[], section: string): any[] {
    return actions.filter((action) => {
      // Si es acción de editar, verificar permiso de update
      if (action.action === 'editar' || action.type === 'edit') {
        return this.canUpdate(section);
      }
      // Si es acción de eliminar, verificar permiso de delete
      if (action.action === 'eliminar' || action.type === 'delete') {
        return this.canDelete(section);
      }
      // Otras acciones siempre permitidas
      return true;
    });
  }
}
